import { useCallback, useEffect } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { raiseEvent } from '@cast/analytics';
import { GetSettingsResponse } from '@cast/types';

import { useUser } from '@cast/infrastructure-auth';
import { useAbility } from '@cast/shared-ability-data-access';
import {
  setOpsPilotOrgProperties,
  setOpsPilotUserProperties,
} from '@cast/infrastructure-analytics';
import { useOrganizationCopilotSettingsMutation } from '../hooks/mutations/useOrganizationCopilotSettingsMutation';

import { useOrganizations } from '@cast/shared-organization-context-data-access';

import { useOpsPilotAcknowledgementFlag } from './useOpsPilotAcknowledgementFlag';
import { useEnableOpsPilotDialog } from './useEnableOpsPilotDialog';
import {
  CAST_PILOT_ASSISTANT_ACK_EVENT_TITLE,
  CAST_PILOT_ASSISTANT_ACK_EVENT_TYPE,
  CAST_PILOT_ASSISTANT_DISABLED_EVENT_TITLE,
  CAST_PILOT_ASSISTANT_ENABLED_EVENT_TITLE,
  CAST_PILOT_ASSISTANT_PRIVACY_URL,
  CAST_PILOT_ASSISTANT_PROP,
  CAST_PILOT_ASSISTANT_TERMS_URL,
  CAST_PILOT_ASSISTANT_TERMS_VERSION,
  CAST_PILOT_ASSISTANT_TOGGLE_EVENT_TYPE,
} from './castPilotAssistant';
import {
  getOrganizationSettingsQueryKey,
  useOrganizationSettingsQuery,
} from '@cast/infrastructure-abilities';
import { useParams } from 'react-router-dom';

/**
 * Orchestrates OpsPilot enablement for the feature-control section:
 * - reads the per-org acknowledgement flag and current settings,
 * - gates OFF -> ON enabling behind the consent dialog (when the flag is on),
 * - records the audit event + PostHog properties on acknowledgement,
 * - keeps the legacy direct-toggle behavior when the flag is off.
 */
export const useOpsPilotEnablement = () => {
  const queryClient = useQueryClient();
  const { currentOrganization } = useOrganizations();
  const { enterpriseId } = useParams<{ enterpriseId: string }>();
  const user = useUser();
  const organizationId = enterpriseId ?? currentOrganization?.id;

  const canUpdateSettings = useAbility().can('update', 'OrganizationSettings');

  const isAckUxEnabled = useOpsPilotAcknowledgementFlag();
  const { openDialog } = useEnableOpsPilotDialog();

  const { data, error, refetch } = useOrganizationSettingsQuery({
    organizationId,
  });
  const { mutate } = useOrganizationCopilotSettingsMutation({
    organizationId: organizationId ?? '',
    additionallyInvalidateOrgs:
      enterpriseId && currentOrganization?.id ? [currentOrganization?.id] : [],
  });

  const isEnabled = data?.copilotSettings?.organizationInitialized
    ? data?.copilotSettings?.enabled ?? false
    : false;
  const acknowledgedAt = isEnabled ? data?.updateTime : undefined;

  const applyToggle = useCallback(
    (checked: boolean) => {
      if (!organizationId) {
        return;
      }
      const queryKey = getOrganizationSettingsQueryKey({ organizationId });
      const previousSettings =
        queryClient.getQueryData<GetSettingsResponse>(queryKey);

      queryClient.setQueryData(
        queryKey,
        (oldData: GetSettingsResponse | undefined): GetSettingsResponse => ({
          ...oldData,
          copilotSettings: {
            ...oldData?.copilotSettings,
            enabled: checked,
            organizationInitialized: true,
          },
        })
      );
      mutate(checked, {
        // Revert the optimistic toggle if the settings update fails.
        onError: () => queryClient.setQueryData(queryKey, previousSettings),
      });
      raiseEvent({
        title: checked
          ? CAST_PILOT_ASSISTANT_ENABLED_EVENT_TITLE
          : CAST_PILOT_ASSISTANT_DISABLED_EVENT_TITLE,
        type: CAST_PILOT_ASSISTANT_TOGGLE_EVENT_TYPE,
        organizationId,
        organizationName: currentOrganization?.name,
        enabled: checked,
        changedAt: new Date().toISOString(),
      });
      setOpsPilotOrgProperties(organizationId, {
        [CAST_PILOT_ASSISTANT_PROP.enabled]: checked,
      });
    },
    [organizationId, currentOrganization?.name, mutate, queryClient]
  );

  const acknowledgeAndEnable = useCallback(() => {
    if (!organizationId) {
      return;
    }
    const acknowledgedAtIso = new Date().toISOString();

    raiseEvent({
      title: CAST_PILOT_ASSISTANT_ACK_EVENT_TITLE,
      type: CAST_PILOT_ASSISTANT_ACK_EVENT_TYPE,
      organizationId,
      organizationName: currentOrganization?.name,
      acknowledgedAt: acknowledgedAtIso,
      termsVersion: CAST_PILOT_ASSISTANT_TERMS_VERSION,
      termsUrl: CAST_PILOT_ASSISTANT_TERMS_URL,
      privacyPolicyUrl: CAST_PILOT_ASSISTANT_PRIVACY_URL,
    });

    setOpsPilotOrgProperties(organizationId, {
      orgID: organizationId,
      orgName: currentOrganization?.name,
      [CAST_PILOT_ASSISTANT_PROP.enabled]: true,
      [CAST_PILOT_ASSISTANT_PROP.acknowledgedAt]: acknowledgedAtIso,
      [CAST_PILOT_ASSISTANT_PROP.acknowledgedBy]: user?.email,
      [CAST_PILOT_ASSISTANT_PROP.termsVersion]:
        CAST_PILOT_ASSISTANT_TERMS_VERSION,
    });
    setOpsPilotUserProperties({
      [CAST_PILOT_ASSISTANT_PROP.userLastAcknowledgedAt]: acknowledgedAtIso,
      [CAST_PILOT_ASSISTANT_PROP.userLastAcknowledgedOrg]: organizationId,
    });

    applyToggle(true);
  }, [organizationId, currentOrganization?.name, user?.email, applyToggle]);

  const openConsentDialog = useCallback(() => {
    openDialog(undefined, {
      onClose: (result) => {
        if (result === 'confirmed') {
          acknowledgeAndEnable();
        }
      },
    });
  }, [openDialog, acknowledgeAndEnable]);

  const handleSwitchToggle = useCallback(
    (_: unknown, checked: boolean) => {
      // Flag off: preserve the legacy direct-toggle behavior.
      if (!isAckUxEnabled) {
        applyToggle(checked);
        return;
      }
      // Flag on: enabling requires acknowledgement; disabling never does.
      if (checked) {
        openConsentDialog();
      } else {
        applyToggle(false);
      }
    },
    [isAckUxEnabled, applyToggle, openConsentDialog]
  );

  // Always push the current enabled value to the PostHog organization group
  // whenever this page loads or the value changes (no-op outside production).
  useEffect(() => {
    if (organizationId && data?.copilotSettings) {
      setOpsPilotOrgProperties(organizationId, {
        [CAST_PILOT_ASSISTANT_PROP.enabled]: isEnabled,
      });
    }
  }, [organizationId, isEnabled, data?.copilotSettings]);

  return {
    isAckUxEnabled,
    isEnabled,
    acknowledgedAt,
    canUpdateSettings,
    error,
    refetch,
    handleSwitchToggle,
  };
};
